/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useLocation } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Container } from '../components/jsx/Container';
import { Section } from '../components/jsx/Section';
import { InfoRow } from '../components/jsx/InfoRow';
import { ServiceTable } from '../components/jsx/ServiceTable';
import {
  getBudgetDataController,
  getBudgetPdfURLController,
} from './controller';
import { maskCpfCnpj, maskPhone } from '../../../services/helpers/mask';
import { formatDate, formatTime } from '../../dateFunctions';
import { QRCode } from '../../qrcode/index';
import { baseURL } from '../../../services/api/api';
import { blobToBase64 } from '../../blobToBase64';
import { getAccessToken } from '../../workshopData';
import { FeedbackModal } from '../../../shared/FeedbackModal';
import { Budget } from '../../../@types/budget';
import styles from '../OrderService/order-service.module.css';
import { ViewImagesModal } from '../OrderService/ViewImagesModal';

export const BudgetReport = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const budgetId = queryParams.get('budgetId');

  const [budget, setBudget] = useState<Budget | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [viewImages, setViewImages] = useState(false);
  const [imageToView, setImageToView] = useState<null | string>(null);

  const accessToken = getAccessToken();

  const fetchBudget = useCallback(async () => {
    const res = await getBudgetDataController(budgetId || '');

    if (res) {
      setBudget(res);
    }
  }, [budgetId]);

  useEffect(() => {
    fetchBudget();
  }, [budgetId, fetchBudget]);

  const handleClickToViewImages = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>,
      img: string,
    ) => {
      e.stopPropagation();
      setViewImages(previous => !previous);
      setImageToView(img);
    },
    [],
  );

  const generateClientSection = () => {
    const items = [
      {
        name: 'Nome',
        value: budget?.name ?? '-',
      },
      {
        name: 'Telefone',
        value: maskPhone(budget?.phone ?? '-'),
      },
      {
        name: 'CPF/CNPJ',
        value: maskCpfCnpj(budget?.document ?? '-'),
      },
    ];
    return (
      <Section title="Cliente">
        <InfoRow items={items} />
      </Section>
    );
  };
  const generateVehicleSection = () => {
    const items = [
      {
        name: 'Modelo',
        value: budget?.vehicle ?? '-',
      },
      {
        name: 'Placa',
        value: budget?.plate ?? '-',
      },
      {
        name: 'km',
        value: budget?.km ? budget.km.toString() : '-',
      },
    ];
    return (
      <Section title="Veículo">
        <InfoRow items={items} />
      </Section>
    );
  };
  const generateServiceSection = () => {
    let discount = 0;
    if (budget) {
      discount = budget.discount ?? 0;

      const parseQuantity = (quantity: any) => {
        if (typeof quantity === 'number') return quantity;
        const parsed = parseFloat(quantity);
        return isNaN(parsed) ? 0 : parsed;
      };

      const calculateUnit = (total: number | undefined, quantity: number) => {
        return quantity > 0 ? (total ?? 0) / quantity : 0;
      };

      const products =
        budget?.child_services
          ?.filter(service => service.type === 'Produto')
          .map(service => {
            const quantity = parseQuantity(service.quantity);
            return {
              name: service.name ?? '-',
              quantity,
              unit: calculateUnit(service.total, quantity),
            };
          }) ?? [];

      const services =
        budget?.child_services
          ?.filter(service => service.type !== 'Produto')
          .map(service => {
            const quantity = parseQuantity(service.quantity);
            return {
              name: service.name ?? '-',
              quantity,
              unit: calculateUnit(service.total, quantity),
            };
          }) ?? [];
      const serviceTotal = services.reduce((acc, currentService) => {
        return acc + currentService.unit * currentService.quantity;
      }, 0);

      const productTotal = products.reduce((acc, currentProduct) => {
        return acc + currentProduct.unit * currentProduct.quantity;
      }, 0);

      const total = serviceTotal + productTotal - discount;

      const imageUrls =
        (budget?.image_urls && budget.image_urls.length > 0
          ? budget.image_urls
          : budget?.images?.map(banner => banner.img_url) ?? []);
      return (
        <Section title="Informações">
          <InfoRow
            items={[
              {
                name: 'Defeitos',
                value: budget?.defect ?? '-',
              },
            ]}
          />

          {imageUrls && imageUrls.length > 0 && (
            <Section title="Imagens">
              <div className={styles['images-wrapper']}>
                {imageUrls.map(img => (
                  <img
                    key={img}
                    src={img}
                    alt="imagem de Orçamento"
                    onClick={e => handleClickToViewImages(e, img)}
                  />
                ))}
              </div>
            </Section>
          )}

          {products.length !== 0 && (
            <Section title="Produtos">
              <ServiceTable
                discount={discount}
                services={products}
                noSummary
                type="product"
              />
            </Section>
          )}
          {services.length !== 0 && (
            <Section title="Serviços">
              <ServiceTable
                discount={discount}
                services={services}
                noSummary
                type="service"
              />
            </Section>
          )}
          <div className={styles.summary}>
            <div className={`${styles.info}`}>
              <span>Subtotal de produtos</span>
              <span>
                {productTotal.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className={`${styles.info}`}>
              <span>Subtotal de serviços</span>
              <span>
                {serviceTotal.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className={`${styles.info}`}>
              <span>Desconto</span>
              <span>
                {discount.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className={`${styles.info}`}>
              <span>Total</span>
              <span>
                {total.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </Section>
      );
    }
  };

  const generateCommentSection = () => {
    const items = [
      {
        name: 'Comentário',
        value: budget?.comment ?? '-',
        fullLine: true,
      },
    ];
    return (
      <Section title="Observações">
        <InfoRow items={items} />
      </Section>
    );
  };

  const generateQRCode = () => {
    if (
      budget &&
      budget.checklist &&
      budget.checklist.banners &&
      budget.checklist.banners.length > 0
    ) {
      return QRCode({
        url: `${baseURL}checklist-report?checklistId=${budget.checklist_id}`,
      });
    }
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  };

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      const res = await getBudgetPdfURLController(budget?.id_budget ?? '');

      if (res?.fileUrl) {
        const response = await fetch(res.fileUrl);

        if (!response.ok) {
          throw new Error('Falha ao baixar o PDF.');
        }

        const blob = await response.blob();

        // Gera o Base64 do PDF
        const base64 = await blobToBase64(blob);

        if (window.ReactNativeWebView) {
          // ✅ Envia para o app
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              name: 'pdfBase64',
              base64, // Ex: data:application/pdf;base64,...
              fileName: `Orçamento-${budget?.BUDGET}.pdf`,
            }),
          );
        } else {
          // Web normal: abre o PDF no navegador
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          const fileName = `Orçamento-${budget?.BUDGET}.pdf`;
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }
      } else {
        console.error('URL do PDF não encontrada na resposta.');
      }
    } catch (error) {
      console.error('Erro ao baixar o PDF:', error);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const validity = budget?.validity ?? '';

  return (
    <Container
      handleDownloadPdf={accessToken ? handleDownloadPdf : undefined}
      workshopName={
        (budget && budget.workshop.fantasy_name) ?? 'Oficina não encontrada'
      }
      documentName={`Orçamento - ${budget?.BUDGET ?? ''}`}
      isDownloadingPdf={isDownloadingPdf}
    >
      {imageToView && (
        <ViewImagesModal
          isOpen={viewImages}
          onClose={() => setViewImages(false)}
          url={imageToView}
        />
      )}
      <FeedbackModal variant="sending" isOpen={isDownloadingPdf} />
      <InfoRow
        items={[
          {
            name: 'Data de Emissão',
            value: `${budget && budget.created_at
                ? `${formatDate(new Date(budget.created_at))} - ${formatTime(
                  new Date(budget.created_at),
                )}`
                : '-'
              }`,
          },
          {
            name: 'Validade',
            value: validity.trim().length > 0 ? validity : '---',
          },
        ]}
      />
      {generateClientSection()}
      {generateVehicleSection()}
      {generateServiceSection()}
      {generateCommentSection()}
      {generateQRCode()}
    </Container>
  );
};
